"use client"

import { Text } from "@/elements"
import React, { FC, useCallback } from "react"
import { ControlledModal } from "@/components"
import { ArrowRightIcon } from "@heroicons/react/24/outline"
import Link from "next/link"
import { ProBadge } from "@/components/Subscriptions/SubscriptionCheckoutModal/components/ProBadge"
import { ampli } from "@/ampli"

interface Props {
  isOpen: boolean
  creativeHandle: string
  onApplyToAJobClick: () => void
  onClose: () => void
}

type StartButton = {
  title: string
  subtitle: string
  isPro: boolean
  onClick?: () => void
  href?: string
  action: "upload-project" | "post-job" | "apply-to-job"
}

const StartButton = ({ title, subtitle, isPro, onClick, href, action }: StartButton) => {
  const Content = (
    <div className="flex w-full flex-row items-center justify-between gap-y-2 rounded-xl border border-bg-3 p-6 shadow-none transition-shadow duration-300 hover:shadow-[0px_4px_24px_0px_rgba(0,0,0,0.10)]">
      <div>
        <Text variant="body2" className="text-fg-2">
          {subtitle}
        </Text>
        <Text variant="h6" className="text-fg-1">
          {title}
        </Text>
      </div>
      {isPro ? <ProBadge /> : <ArrowRightIcon className="size-[24px]" />}
    </div>
  )

  const handleClick = useCallback(() => {
    ampli.entryActionChoose({
      action,
    })
    onClick?.()
  }, [action, onClick])

  if (href) {
    return (
      <Link href={href} onClick={handleClick}>
        {Content}
      </Link>
    )
  }

  return (
    <div onClick={handleClick} className="cursor-pointer">
      {Content}
    </div>
  )
}

export const WhereDoYouWantToStartModal: FC<Props> = ({ onApplyToAJobClick, isOpen, creativeHandle, onClose }) => {
  const buttons: StartButton[] = [
    {
      title: "Upload a project",
      subtitle: "Build your portfolio",
      isPro: false,
      href: `/${creativeHandle}/projects/form`,
      action: "upload-project",
    },
    {
      title: "Post a job",
      subtitle: "Hire",
      isPro: false,
      href: `/hire?postAJob=true`,
      action: "post-job",
    },
    {
      title: "Apply to a job",
      subtitle: "Find work",
      isPro: true,
      onClick: onApplyToAJobClick,
      action: "apply-to-job",
    },
  ]

  return (
    <ControlledModal
      open={isOpen}
      variant="default"
      onClose={onClose}
      zIndex={102}
      body={
        <div className="pt-8">
          <Text variant="body2" className="text-center text-fg-2">
            All done!
          </Text>
          <Text variant="h6" className="mb-6 text-center text-fg-1">
            Where do you want to start?
          </Text>
          <div className="flex flex-col gap-y-2">
            {buttons.map((button, index) => (
              <StartButton key={index} {...button} />
            ))}

            <Text
              onClick={() => {
                ampli.entryActionChoose({
                  action: "explore",
                })
                onClose()
              }}
              variant="body2"
              className="mt-2 text-center text-fg-2">
              I just want to explore
            </Text>
          </div>
        </div>
      }
    />
  )
}
